import * as clientDb from "../db/clientDb";
import type { DashboardGreeting, DashboardVital, RecipeSuggestion } from "../types/dashboard";
import type { GreetingInput, RecipeCandidate } from "./offlineFallback";

const AI_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface ItemRef {
    id: number;
    quantity: number;
    daysLeft: number;
}

interface GreetingVitalRefs {
    slot: string;
    totalItems?: number;
    items: ItemRef[];
}

interface RecipeRefs {
    pool: ItemRef[];
    items: ItemRef[];
}

interface CacheEntry<T> {
    value: T;
    generatedAt: string;
    refs: unknown;
}

function getToday(now: Date): Date {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    return today;
}

function daysUntilExpiry(expiryDate: string, today: Date): number {
    const expiry = new Date(`${expiryDate}T00:00:00`);
    return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function itemRefFromLocalItem(item: clientDb.LocalItem, today: Date): ItemRef {
    return {
        id: item.id,
        quantity: item.quantity,
        daysLeft: item.expiry_date ? daysUntilExpiry(item.expiry_date, today) : 0,
    };
}

function isExpired(generatedAt: string, now: number): boolean {
    const ts = Date.parse(generatedAt.replace(" ", "T"));
    if (!Number.isFinite(ts)) return true;
    return now - ts >= AI_CACHE_TTL_MS;
}

function inferCitedItemRefs(
    text: string,
    candidates: Array<{ id: number; name: string; quantity: number; daysLeft: number | null }>,
): ItemRef[] {
    const normalized = text.toLowerCase();
    const cited: ItemRef[] = [];
    const seen = new Set<number>();
    const sorted = [...candidates].sort((a, b) => b.name.length - a.name.length);

    for (const item of sorted) {
        const name = item.name.toLowerCase().trim();
        if (name.length < 2 || seen.has(item.id)) continue;
        if (normalized.includes(name)) {
            cited.push({
                id: item.id,
                quantity: item.quantity,
                daysLeft: item.daysLeft ?? 0,
            });
            seen.add(item.id);
        }
    }

    return cited;
}

function refsAreValid(stored: ItemRef[], allItems: clientDb.LocalItem[], now: number): boolean {
    const today = getToday(new Date(now));
    const byId = new Map(allItems.map((item) => [item.id, item]));
    for (const ref of stored) {
        const item = byId.get(ref.id);
        if (!item) return false;
        const current = itemRefFromLocalItem(item, today);
        if (current.quantity !== ref.quantity || current.daysLeft !== ref.daysLeft) return false;
    }
    return true;
}

export async function getCachedGreeting(ctx: GreetingInput, now: number): Promise<DashboardGreeting | null> {
    const stored = await clientDb.getAiCache("greeting");
    if (!stored) return null;
    const parsed = JSON.parse(stored) as CacheEntry<DashboardGreeting>;
    if (isExpired(parsed.generatedAt, now)) return null;

    const refs = parsed.refs as GreetingVitalRefs;
    if (refs.slot !== ctx.slot) return null;
    if (refs.totalItems !== undefined && refs.totalItems !== ctx.totalItems) return null;
    if (!refsAreValid(refs.items, ctx.allItems, now)) return null;

    return parsed.value;
}

export async function setCachedGreeting(
    value: DashboardGreeting,
    generatedAt: string,
    ctx: GreetingInput,
): Promise<void> {
    const refs: GreetingVitalRefs = {
        slot: ctx.slot,
        totalItems: ctx.totalItems,
        items: inferCitedItemRefs(`${value.headline} ${value.line}`, ctx.expiringItems),
    };
    await clientDb.setAiCache(
        "greeting",
        JSON.stringify({ value, generatedAt, refs } satisfies CacheEntry<DashboardGreeting>),
    );
}

export async function getCachedVital(ctx: GreetingInput, now: number): Promise<DashboardVital | null> {
    const stored = await clientDb.getAiCache("vital");
    if (!stored) return null;
    const parsed = JSON.parse(stored) as CacheEntry<DashboardVital>;
    if (isExpired(parsed.generatedAt, now)) return null;

    const refs = parsed.refs as GreetingVitalRefs;
    if (refs.slot !== ctx.slot) return null;
    if (!refsAreValid(refs.items, ctx.allItems, now)) return null;

    return parsed.value;
}

export async function setCachedVital(value: DashboardVital, generatedAt: string, ctx: GreetingInput): Promise<void> {
    const refs: GreetingVitalRefs = {
        slot: ctx.slot,
        items: inferCitedItemRefs(`${value.value} ${value.label}`, ctx.expiringItems),
    };
    await clientDb.setAiCache(
        "vital",
        JSON.stringify({ value, generatedAt, refs } satisfies CacheEntry<DashboardVital>),
    );
}

export async function getCachedRecipe(candidates: RecipeCandidate[], now: number): Promise<RecipeSuggestion | null> {
    const stored = await clientDb.getAiCache("recipe");
    if (!stored) return null;
    const parsed = JSON.parse(stored) as CacheEntry<RecipeSuggestion | null>;
    if (isExpired(parsed.generatedAt, now)) return null;

    const refs = parsed.refs as RecipeRefs;
    if (refs.pool.length !== candidates.length) return null;
    for (let i = 0; i < candidates.length; i++) {
        const c = candidates[i];
        const ref = refs.pool[i];
        if (ref.id !== c.id || ref.quantity !== c.quantity || ref.daysLeft !== (c.daysLeft ?? 0)) return null;
    }

    const byId = new Map(candidates.map((c) => [c.id, c]));
    for (const ref of refs.items) {
        const c = byId.get(ref.id);
        if (!c || c.quantity !== ref.quantity || (c.daysLeft ?? 0) !== ref.daysLeft) return null;
    }

    return parsed.value;
}

export async function setCachedRecipe(
    value: RecipeSuggestion | null,
    generatedAt: string,
    candidates: RecipeCandidate[],
): Promise<void> {
    const pool = candidates.map((c) => ({ id: c.id, quantity: c.quantity, daysLeft: c.daysLeft ?? 0 }));
    const byId = new Map(candidates.map((c) => [c.id, c]));
    const items: ItemRef[] = [];
    if (value) {
        for (const ing of value.ingredients) {
            if (typeof ing.itemId !== "number" || ing.itemId <= 0) continue;
            const c = byId.get(ing.itemId);
            if (!c) continue;
            items.push({ id: c.id, quantity: c.quantity, daysLeft: c.daysLeft ?? 0 });
        }
    }
    const refs: RecipeRefs = { pool, items };
    await clientDb.setAiCache(
        "recipe",
        JSON.stringify({ value, generatedAt, refs } satisfies CacheEntry<RecipeSuggestion | null>),
    );
}
